import React, { useState, useEffect, useRef } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import Header from "../../components/header";
import toast, { Toaster } from 'react-hot-toast';
import { ArrowRight } from 'lucide-react';
import { apiCall } from '../../utils/api';

function UPIIntent() {
    const location = useLocation();
    const navigate = useNavigate();
    const [paymentUrl, setPaymentUrl] = useState('');
    const [selectedApp, setSelectedApp] = useState('');
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [amount, setAmount] = useState('');
    const [paymentOrderID, setPaymentOrderID] = useState(null);
    const [utilityOrderID, setUtilityOrderID] = useState(null);

    // Timer related refs and states
    const timerRef = useRef(null);
    const statusCheckRef = useRef(null);
    const [isStatusCheckActive, setIsStatusCheckActive] = useState(false);
    const [lastStatusData, setLastStatusData] = useState(null);
    const [checkCount, setCheckCount] = useState(0);
    const [timeRemaining, setTimeRemaining] = useState(120);
    const MAX_CHECKS = 24; // 2 minutes / 5 seconds = 24 checks

    const getUPIParams = () => {
        try {
            const urlParams = new URLSearchParams(paymentUrl.split('?')[1]);
            return {
                pa: urlParams.get('pa') || '',
                pn: urlParams.get('pn') || '',
                tr: urlParams.get('tr') || '',
                am: urlParams.get('am') || '',
                cu: urlParams.get('cu') || 'INR',
                mc: urlParams.get('mc') || ''
            };
        } catch (error) {
            return {};
        }
    };

    const upiParams = getUPIParams();
    const intentParams = new URLSearchParams({
        pa: upiParams.pa,
        pn: upiParams.pn,
        tr: upiParams.tr,
        am: upiParams.am,
        cu: upiParams.cu,
        mc: upiParams.mc
    });

    const apps = [
        {
            id: 'phonepe',
            name: 'PhonePe',
            intentUrl: `phonepe://pay?${intentParams.toString()}`,
            fallbackPackage: 'com.phonepe.app',
            icon: (
                <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 7 34 34" width="32" height="32">
                    <path d="M16.6 7.4a16.6 16.6 0 0 0 0 33.2 16.6 16.6 0 0 0 0-33.2z" fill="#673ab7" />
                    <path d="M23.4 18.9h-3l-1.6-2.7-2.5-4.3-.4-.2h-2.1c-.3 0-.5.3-.4.6l4.9 6.6H9.7c-.2 0-.4.2-.4.4v1.9c0 .2.2.4.4.4h2v5c0 .8.2 1.5.5 2.2a5 5 0 0 0 6.6 2.2v4c0 .2.2.4.4.4H21c.2 0 .4-.2.4-.4V21.6h1.9c.2 0 .4-.2.4-.4v-1.9c.1-.2-.1-.4-.3-.4zm-4.6 7.7c0 1.2-1 2.2-2.2 2.2-1.2 0-2.2-1-2.2-2.2v-5h4.4v5z" fill="#fff" />
                </svg>
            )
        },
        {
            id: 'google_pay',
            name: 'Google Pay',
            intentUrl: `tez://upi/pay?${intentParams.toString()}`,
            fallbackPackage: 'com.google.android.apps.nbu.pay',
            icon: (
                <svg xmlns="http://www.w3.org/2000/svg" width="32" height="32" viewBox="0 0 256 218" preserveAspectRatio="xMidYMid">
                    <path d="M233 42c-26-14-58-6-73 20l-36 63c-11 19 3 25 18 34l35 21c12 7 28 2 35-10l37-65c13-22 6-50-16-63Z" fill="#EA4335" />
                    <path d="m191 69-36-21c-19-11-30-11-38 1l-52 91c-15 25-6 57 19 72 22 12 50 5 63-17l53-92c7-12 3-28-9-34Z" fill="#FBBC04" />
                    <path d="M198 22 173 8c-28-16-63-7-79 21l-47 82c-7 12-3 27 9 34l28 16c14 8 31 4 39-10l54-94c12-19 36-26 56-15l-35-20Z" fill="#34A853" />
                    <path d="M101 52 74 37c-12-7-27-3-34 9L8 102c-16 27-7 62 21 78l20 12 25 14 11 6a44 44 0 0 1-14-58l9-15 30-53c7-12 3-27-9-34Z" fill="#4285F4" />
                </svg>
            )
        },
        {
            id: 'paytm',
            name: 'Paytm',
            intentUrl: `paytmmp://pay?${intentParams.toString()}`,
            fallbackPackage: 'net.one97.paytm',
            icon: (
                <svg viewBox="-148 32.5 484 484" width="32" height="32" xmlns="http://www.w3.org/2000/svg">
                    <path d="M230 243l4-3c14-12 32-10 43 4h3l3-3c9-9 22-11 33-6 12 5 19 15 19 28v86c0 10-6 16-17 16l-12 1c-4 0-5-1-5-6v-84-3c0-7-2-9-9-10-5 0-9 3-10 9v77c0 10-6 16-16 16-6 0-13 3-16-1-3-3-1-10-1-15v-75c0-8-6-13-13-10-5 2-6 6-6 10v75c-1 10-7 16-16 16h-12c-5 1-6 0-6-5V242v-5c0-2 1-3 3-3h28c2 0 3 1 3 3v6z" fill="#02b9ef" />
                    <path d="M18 297v41c0 18-10 27-27 27l-24 1c-16-1-27-11-28-27v-34c1-16 13-28 29-28h13c5 0 6-2 6-6s-2-6-6-6h-13c-11 0-17-6-17-17 0-4-2-10 1-13 2-2 8-1 12-1h34c12 0 20 8 20 20v43zm-31 23v-5c0-10 0-10-10-10-5 1-8 3-8 8v13c0 7 3 9 14 9 7 1 3-5 4-8v-7z" fill="#06306f" />
                    <path d="M107 287v46c0 11-3 22-15 28-4 3-9 4-15 4l-34 1c-3 0-3-1-3-4v-12c0-8 6-15 14-15h16c4 0 6-2 6-6 0-5-2-6-6-6-7-1-14 0-21-2-12-3-21-13-21-25v-59c0-2 0-3 3-3h24c4 0 3 2 3 5v44c0 6 3 9 9 10 6 0 9-3 9-10v-44c0-4 1-5 5-5h22c4 0 4 2 4 5v48zM-148 309v-49c0-16 10-26 26-26 10 0 21-1 31 1 14 3 22 13 22 27v43c0 19-11 30-29 30h-16c-3 0-4 1-4 4v12c0 8-6 14-15 14-5 0-11 2-14 0-3-3-1-10-1-14v-42zm30-24v9c0 11 0 11 12 10 4 0 7-2 7-7v-17c0-16-3-15-16-15-2 0-3 1-3 3v17z" fill="#06306f" />
                    <path d="M135 309v-40c0-3-1-4-4-4-4 0-10 2-13-1s-1-9-1-14v-1c0-4-1-10 1-13s8-2 12-3c8-2 15-7 20-14 4-4 8-7 14-9 3 0 5 0 5 4v17c0 3 0 3 3 3h12c2 0 3 1 3 3v25c0 2-1 4-3 3h-2c-4 1-10-2-12 1-3 3-1 9-1 13v82c0 4-1 5-5 5l-11-1c-11 0-18-7-18-18v-38z" fill="#02b9ef" />
                </svg>
            )
        },
        {
            id: 'bhim',
            name: 'UPI (BHIM)',
            intentUrl: `upi://pay?${intentParams.toString()}`,
            fallbackPackage: '',
            icon: (
                <svg viewBox="0 0 552 552" width="32" height="32" xmlns="http://www.w3.org/2000/svg">
                    <path d="M332 0L471 276 179 552z" fill="#098041" />
                    <path d="M234 0L373 276 81 552z" fill="#E97626" />
                </svg>
            )
        }
    ];

    useEffect(() => {
        console.log("state", location.state);
        // Get the payment URL from navigation state
        if (location.state && location.state.url) {
            setPaymentUrl(location.state.url);
            setPaymentOrderID(location.state.payment_order_id);
            setUtilityOrderID(location.state.client_order_id);
            // Extract amount from URL
            const amountMatch = location.state.url.match(/am=([\d.]+)/);
            if (amountMatch) {
                setAmount(amountMatch[1]);
            }
        } else {
            setError('Payment URL not found');
        }
    }, [location]);

    // Cleanup timer on unmount
    useEffect(() => {
        return () => {
            if (timerRef.current) {
                clearInterval(timerRef.current);
            }
            if (statusCheckRef.current) {
                clearTimeout(statusCheckRef.current);
            }
        };
    }, []);

    const checkStatus = async () => {
        try {
            let result;

            if (utilityOrderID) {
                // Check utility order status
                result = await apiCall(
                    "client-order-id-status",
                    {
                        client_order_id: utilityOrderID
                    },
                    true
                );
            } else {
                // Check payment order status
                result = await apiCall(
                    "payment-order-id-status",
                    {
                        payment_order_id: paymentOrderID
                    },
                    true
                );
            }

            // Store the last status data
            setLastStatusData(result);

            // Check if status is successful or failed
            if (result.status) {
                // Success - stop checking and redirect
                if (result.order_details.status === "Success") {
                    stopStatusCheck();
                    navigateToPaymentStatus(result, true);
                    return true;
                } else {
                    return false;
                }
            } else if (result.order_details && result.order_details.status === "Failed") {
                // Failed - stop checking and redirect
                stopStatusCheck();
                navigateToPaymentStatus(result, false);
                return true;
            }

            return false;
        } catch (error) {
            console.error('Status check error:', error);
            // Don't stop checking on error, just log it
            return false;
        }
    };

    const stopStatusCheck = () => {
        setIsStatusCheckActive(false);
        setTimeRemaining(0);
        if (timerRef.current) {
            clearInterval(timerRef.current);
            timerRef.current = null;
        }
        if (statusCheckRef.current) {
            clearTimeout(statusCheckRef.current);
            statusCheckRef.current = null;
        }
        setLoading(false);
    };

    const navigateToPaymentStatus = (result, isSuccess) => {
        let statusData;

        if (utilityOrderID) {
            // Utility order data
            const utility_payload = location.state.utility_payload;
            statusData = {
                status: isSuccess ? result.order_details?.status || "Success" : "Failed",
                amount: utility_payload.amount,
                number: utility_payload.number,
                order_id: result.order_details?.order_id || '',
                operator_ref: result.order_details?.operator_ref || '',
                cashback: result.order_details?.cashback || '',
                message: result.message || (isSuccess ? 'Payment successful' : 'Payment failed')
            };
        } else {
            // Payment order data
            statusData = {
                status: isSuccess ? result.order_details?.status || "Success" : "Failed",
                amount: amount,
                number: result.order_details?.number || '',
                order_id: result.order_details?.order_id || '',
                operator_ref: result.order_details?.operator_ref || '',
                cashback: result.order_details?.cashback || '',
                message: result.message || (isSuccess ? 'Payment successful' : 'Payment failed')
            };
        }

        navigate("/payment-status", {
            state: statusData
        });
    };

    const startStatusCheck = () => {
        setIsStatusCheckActive(true);
        setCheckCount(0);
        setLastStatusData(null);
        setTimeRemaining(120); // Reset to 2 minutes

        // Immediate first check
        checkStatus().then((shouldStop) => {
            if (shouldStop) return;

            // Start interval for subsequent checks
            timerRef.current = setInterval(async () => {
                // Update time remaining
                setTimeRemaining(prev => {
                    const newTime = prev - 1;
                    if (newTime <= 0) {
                        // Time's up - stop checking
                        stopStatusCheck();

                        // Handle timeout
                        if (lastStatusData) {
                            const isSuccess = lastStatusData.status &&
                                lastStatusData.order_details?.status !== "Failed";
                            navigateToPaymentStatus(lastStatusData, isSuccess);
                        } else {
                            const utility_payload = location.state?.utility_payload;
                            navigate("/payment-status", {
                                state: {
                                    status: 'Timeout',
                                    amount: amount || utility_payload?.amount || '',
                                    number: utility_payload?.number || '',
                                    order_id: '',
                                    operator_ref: '',
                                    cashback: '',
                                    message: 'Payment verification timeout. Please check your transaction status.'
                                }
                            });
                        }
                        return 0;
                    }
                    return newTime;
                });

                setCheckCount(prev => prev + 1);

                // Only perform check if we haven't reached max checks
                if (checkCount < MAX_CHECKS) {
                    await checkStatus();
                }
            }, 1000); // Changed from 5000 to 1000 (1 second interval)
        });
    };

    const resetToFreshState = () => {
        // Stop any ongoing status checks
        stopStatusCheck();
        // Reset loading state
        setLoading(false);
        // Reset selected app
        setSelectedApp('');
        // Clear any errors
        setError('');
        // Reset status check states
        setIsStatusCheckActive(false);
        setCheckCount(0);
        setLastStatusData(null);
        // Clear any timers
        if (timerRef.current) {
            clearInterval(timerRef.current);
            timerRef.current = null;
        }
        if (statusCheckRef.current) {
            clearTimeout(statusCheckRef.current);
            statusCheckRef.current = null;
        }
    };

    const handlePayment = async (app) => {
        setSelectedApp(app.id);
        setLoading(true);
        setError('');

        try {
            // Try to open the app with intent URL
            window.location.href = app.intentUrl;

            // Set a timeout to check if app is installed
            // If the app is installed, the intent will handle it and we start status check
            // If not installed, we show toast and reset
            const appInstallCheckTimeout = setTimeout(() => {
                // If we're still loading after 2 seconds, the app didn't open
                // This means app is not installed
                if (loading) {
                    // Reset everything to fresh state
                    resetToFreshState();
                    toast.error(`${app.name} is not installed on your device`);
                }
            }, 2000);

            // Store the timeout reference to clear it if app opens successfully
            statusCheckRef.current = appInstallCheckTimeout;

            // Start status checking after a delay (if app opens successfully)
            setTimeout(() => {
                // Clear the app install check timeout since app opened
                if (statusCheckRef.current) {
                    clearTimeout(statusCheckRef.current);
                    statusCheckRef.current = null;
                }
                // Start status checking
                startStatusCheck();
            }, 3000);

            // Fallback to Play Store if app is not installed (except for generic UPI)
            if (app.id !== 'bhim' && app.fallbackPackage) {
                setTimeout(() => {
                    // This will only execute if app didn't open and we're still in loading state
                    resetToFreshState();
                    toast.error(`${app.name} is not installed on your device`);
                }, 2000);
            } else if (app.id === 'bhim') {
                // For generic UPI, show toast if not installed
                setTimeout(() => {
                    resetToFreshState();
                    toast.error(`${app.name} is not installed on your device`);
                }, 2000);
            }
        } catch (error) {
            console.error('Payment error:', error);
            resetToFreshState();
            toast.error('Failed to open payment app. Please try again.');
        }
    };

    if (error && !paymentUrl) {
        return (
            <div className="min-h-screen bg-gray-100 flex items-center justify-center p-4">
                <div className="bg-white rounded-lg shadow-xl p-6 max-w-md w-full text-center">
                    <div className="text-red-600 text-6xl mb-4">⚠️</div>
                    <h2 className="text-xl font-semibold text-gray-800 mb-2">Error</h2>
                    <p className="text-gray-600 mb-4">{error}</p>
                    <button
                        onClick={() => navigate(-1)}
                        className="bg-blue-600 text-white px-6 py-2 rounded-lg hover:bg-blue-700 transition"
                    >
                        Go Back
                    </button>
                </div>
            </div>
        );
    }

    return (
        <div className="min-h-screen flex flex-col bg-white">
            <Header title={"Add Money"} showBack={true} />
            <div className="flex-1 flex flex-col p-4">
                {/* Amount Display */}
                <div className="bg-white rounded-lg shadow-md border border-gray-100 p-4 mb-6">
                    <div className="text-center">
                        <p className="text-gray-600 text-sm mb-1">Payment Amount</p>
                        <p className="text-3xl font-bold text-gray-800">
                            ₹{amount || upiParams.am || '10.00'}
                        </p>
                        <p className="text-xs text-gray-500 mt-1">
                            Pay to: {upiParams.pn || 'Merchant'}
                        </p>
                    </div>
                </div>

                {/* Timer Display */}
                {isStatusCheckActive && (
                    <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 mb-4 text-center">
                        <p className="text-sm text-blue-700">
                            ⏱️ Checking payment status... ({timeRemaining} seconds remaining)
                        </p>
                        <div className="w-full bg-blue-200 rounded-full h-2 mt-2">
                            <div
                                className="bg-blue-600 h-2 rounded-full transition-all duration-1000"
                                style={{ width: `${(timeRemaining / 120) * 100}%` }}
                            ></div>
                        </div>
                    </div>
                )}

                {/* Payment Options */}
                <div className="space-y-3 mb-6">
                    {apps.map((app) => (
                        <button
                            key={app.id}
                            onClick={() => handlePayment(app)}
                            disabled={loading && selectedApp === app.id}
                            className="w-full shadow-md p-2 bg-white rounded-lg shadow-md border border-gray-100 flex items-center justify-between hover:shadow-lg transition-all duration-200 group"
                        >
                            <div className="flex items-center space-x-4">
                                <div className="w-12 h-12 flex items-center justify-center">
                                    {app.icon}
                                </div>
                                <div className="text-left">
                                    <p className="font-semibold text-md">{app.name}</p>
                                </div>
                            </div>
                            {loading && selectedApp === app.id ? (
                                <svg
                                    className="animate-spin h-5 w-5 text-blue-600"
                                    xmlns="http://www.w3.org/2000/svg"
                                    fill="none"
                                    viewBox="0 0 24 24"
                                >
                                    <circle
                                        className="opacity-25"
                                        cx="12"
                                        cy="12"
                                        r="10"
                                        stroke="currentColor"
                                        strokeWidth="4"
                                    />
                                    <path
                                        className="opacity-75"
                                        fill="currentColor"
                                        d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                                    />
                                </svg>
                            ) : (
                                <ArrowRight className="w-5 h-5 text-gray-400 group-hover:text-blue-600 group-hover:translate-x-1 transition-all duration-200" />
                            )}
                        </button>
                    ))}
                </div>
            </div>

            {/* Loading Overlay */}
            {loading && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
                    <div className="bg-white rounded-lg p-6 text-center max-w-sm mx-4 flex flex-col items-center justify-center">

                        <svg
                            className="animate-spin h-8 w-8 text-blue-600"
                            xmlns="http://www.w3.org/2000/svg"
                            fill="none"
                            viewBox="0 0 24 24"
                        >
                            <circle
                                className="opacity-25"
                                cx="12"
                                cy="12"
                                r="10"
                                stroke="currentColor"
                                strokeWidth="4"
                            />
                            <path
                                className="opacity-75"
                                fill="currentColor"
                                d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                            />
                        </svg>

                        <p className="text-gray-800 font-semibold mt-4">
                            Opening payment app...
                        </p>
                        <p className="text-gray-600 text-sm mt-2">
                            Please wait while we redirect you
                        </p>
                    </div>
                </div>
            )}
            <Toaster position="bottom-center" />
        </div>
    );
}

export default UPIIntent;